import { format } from 'date-fns';
import type { AppointmentOverview } from '../services/appointment';

/** A date of birth is a calendar day stored as UTC midnight; read it as that day, in any timezone. */
export function birthDay(value?: string): Date | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function ageOn(dob: Date, on: Date = new Date()): number {
  let age = on.getFullYear() - dob.getFullYear();
  if (on.getMonth() < dob.getMonth() || (on.getMonth() === dob.getMonth() && on.getDate() < dob.getDate())) age--;
  return age;
}

export type StepState = 'done' | 'current' | 'failed' | 'todo';

/** Where the visit stands, as the steps a practice recognizes. */
export function visitSteps(o: AppointmentOverview): { label: string; state: StepState; detail?: string }[] {
  const recorded = o.appointment.recordingStatus === 'completed';
  const recordFailed = o.appointment.recordingStatus === 'failed';
  const transcribed = o.appointment.transcriptionStatus === 'completed';
  const transcribeFailed = o.appointment.transcriptionStatus === 'failed';
  const transcribing = recorded && !transcribed && !transcribeFailed;
  if (o.appointment.visitStatus && o.appointment.visitStatus !== 'scheduled') {
    // The visit did not happen: show that instead of capture steps that will never come.
    return [
      { label: 'Booked', state: 'done', detail: o.appointment.createdAt ? format(new Date(o.appointment.createdAt), 'MMM d') : undefined },
      {
        label: o.appointment.visitStatus === 'cancelled' ? 'Cancelled' : 'No-show',
        state: 'failed',
        detail: o.appointment.visitStatusChangedAt ? format(new Date(o.appointment.visitStatusChangedAt), 'MMM d, h:mm a') : undefined,
      },
    ];
  }
  return [
    { label: 'Booked', state: 'done', detail: o.appointment.createdAt ? format(new Date(o.appointment.createdAt), 'MMM d') : undefined },
    {
      label: 'Recorded',
      state: recorded ? 'done' : recordFailed ? 'failed' : o.appointment.recordingStatus === 'recording' ? 'current' : 'todo',
      detail: o.recordedBy?.name,
    },
    { label: 'Transcribed', state: transcribed ? 'done' : transcribeFailed ? 'failed' : transcribing ? 'current' : 'todo' },
    { label: 'Note', state: o.note.state !== 'none' ? 'done' : transcribed ? 'current' : 'todo' },
    {
      label: 'Signed',
      state: o.note.state === 'signed' ? 'done' : o.note.state === 'draft' ? 'current' : 'todo',
      detail: o.note.state === 'signed' ? [o.note.signedBy, o.note.signedAt && format(new Date(o.note.signedAt), 'MMM d, h:mm a')].filter(Boolean).join(' · ') : undefined,
    },
  ];
}

